import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import set from 'lodash/set';
import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';
import { DebouncedFunc, isEqual } from 'lodash';
// routing
import { DEFAULT_TOKEN, DEFAULT_TOKEN_SHORT_NAME } from 'app/routing/registration';
// modules
import { LStorage } from 'modules/Storage';
import FormatService from 'modules/Format';
import Settings from 'modules/Settings/Settings';
import AppService from 'modules/App';
import LocaleService from 'modules/Localization';
import Logger from 'modules/Logger';
import TradingChart from 'modules/TradingChart';
import AnalyticsService from 'modules/Analytics';
// constants
import { TOTAL_ALLOWED_LAYOUTS } from 'modules/Settings/constants';
import { broker, THEME_KEYS, UPSTOX_LAYOUT_NAME } from 'modules/TradingChart/consts';
import { EVENTS } from 'modules/Analytics/consts';
// utils
import { getSystemDefaultTheme } from 'utils/helpers/systemDefaultTheme';
import { getUpdatedSavedStudyTemplate, updateSymbolWithTVToken, removeOlderDrawingSourcesIfNeeded } from 'modules/TradingChart/components/utils/drawingUtils';
import { getStringSizeInKb } from 'modules/TradingChart/components/utils/ChartSizeCalculator';
// service
import TradingChartService from 'modules/TradingChart/service';
// typings
import { StudyTemplateContentType } from 'modules/ChartSync/typings';
import { SettingsType } from 'modules/Settings/typings';
import { DRAWINGS_SAVE_THROTTLE, LINE_TOOL_GROUPS_THROTTLE, UPSTOX_LAYOUT_DEFAULT_CHART_CONTENT } from './constants';
import TrackTVChartSaveEvents from '../../services/trackSaveEvents';
import { DEFAULT_CHART_INTERVAL_TV } from '../../services/format/format';
import { RecentLayoutService } from '.';
import {
  IExternalSaveLoadAdapter,
  ChartMetaInfo,
  ChartData,
  StudyTemplateMetaInfo,
  StudyTemplateData,
  ThemeName,
  ChartTemplate,
  LineToolsAndGroupsState,
  LineToolsAndGroupsLoadRequestType,
  EntityId,
  LineToolState,
  ResolutionString,
} from '../../../sources/charting_library.min';
import { NotificationType } from '../../../sources/charting_library';

class SaveLoadAdapter implements IExternalSaveLoadAdapter {
  exchangeToken = '';
  accumulatedDrawings: Map<
    string,
    Map<EntityId, LineToolState | null>
  > = new Map();

  debouncedSaveDrawings: DebouncedFunc<() => Promise<void>>;

  private loadLineToolsRequestCache: {
    promiseCache: {
      [key: string]: Promise<Partial<LineToolsAndGroupsState> | null>;
    };
    lastRequestTime: number;
  } = {
    promiseCache: {},
    lastRequestTime: 0,
  };

  constructor(exchangeToken: string) {
    this.exchangeToken = exchangeToken;
    // Debounce the saveDrawings method with a delay of 500ms
    this.debouncedSaveDrawings = debounce(
      this.saveDrawings.bind(this),
      DRAWINGS_SAVE_THROTTLE,
    );
  }

  getChartInfoStorage() {
    const rawData = LStorage.getItem<
      { [key in string]: ChartMetaInfo & { theme?: ThemeName } }
    >('TV_CHART_LAYOUT');
    return rawData;
  }

  setChartInfoStorage(
    chartId: string | number,
    chartData?: ChartMetaInfo & { theme?: ThemeName },
  ) {
    const chartsList = this.getChartInfoStorage() || {};
    if (chartData) {
      chartsList[chartId] = chartData;
    }
    LStorage.setItem('TV_CHART_LAYOUT', chartsList);
  }

  deleteChartInfoStorage(layoutId: string | number) {
    const chartsList = this.getChartInfoStorage() || {};
    delete chartsList[layoutId];
    LStorage.setItem('TV_CHART_LAYOUT', chartsList);
  }

  getChartStudyTemplatesStorage() {
    return LStorage.getItem<{ [key in string]: StudyTemplateData }>(
      'TV_CHART_STUDY_TEMPLATES',
    );
  }

  setChartStudyTemplatesStorage(
    templateName: string,
    studyTemplateData?: StudyTemplateData,
  ) {
    const studyTemplatesList = this.getChartStudyTemplatesStorage() || {};
    if (studyTemplateData) {
      studyTemplatesList[templateName] = studyTemplateData;
    }
    LStorage.setItem('TV_CHART_STUDY_TEMPLATES', studyTemplatesList);
  }

  deleteChartStudyTemplatesStorage(templateName: string) {
    const studyTemplatesList = this.getChartStudyTemplatesStorage() || {};
    delete studyTemplatesList[templateName];
    LStorage.setItem('TV_CHART_STUDY_TEMPLATES', studyTemplatesList);
  }

  getChartDrawingsStorage() {
    const rawData = LStorage.getItem<{ [key in string]: string }>(
      'TV_CHART_DRAWINGS',
    );
    return rawData;
  }

  setChartDrawingsStorage(
    drawingKey: string | number,
    data: { id: string; content: string },
  ) {
    const drawingsData = this.getChartDrawingsStorage() || {};
    set(drawingsData, drawingKey, data);
    LStorage.setItem('TV_CHART_DRAWINGS', drawingsData);
  }

  deleteChartDrawingsStorage(layoutId: string | number) {
    const drawings = this.getChartDrawingsStorage() || {};
    delete drawings[layoutId];
    LStorage.setItem('TV_CHART_DRAWINGS', drawings);
  }

  async getAllCharts(): Promise<ChartMetaInfo[]> {
    if (Settings.getIsChartSettingsSaveEnabled()) {
      let chartsList: ChartMetaInfo[] = [];
      const chartSettings = await Settings.getChartSettingsList(
        SettingsType.SAVING_CHART_LAYOUT,
      );
      if (chartSettings && 'savedLayouts' in chartSettings) {
        chartsList = (chartSettings.savedLayouts as unknown) as ChartMetaInfo[];
      }
      this.setUpstoxLayoutIdInStore(chartsList);
      TrackTVChartSaveEvents.sendTotalChartSavedMixpanelEvent(
        chartsList?.length ?? 0,
        true,
      );
      return chartsList;
    }
    return new Promise(resolve => {
      const chartsList: ChartMetaInfo[] = Object.values(
        this.getChartInfoStorage() || {},
      );
      this.setUpstoxLayoutIdInStore(chartsList);
      TrackTVChartSaveEvents.sendTotalChartSavedMixpanelEvent(
        chartsList?.length ?? 0,
        false,
      );
      resolve(chartsList);
    });
  }

  setUpstoxLayoutIdInStore(chartsList: ChartMetaInfo[]) {
    const upstoxLayout =
        chartsList.find(chart => chart.name === UPSTOX_LAYOUT_NAME) || null;
      if (upstoxLayout) {
        TradingChart.setUpstoxLayoutId(upstoxLayout?.id);
      }
  }

  async removeChart(layoutId: string | number): Promise<void> {
    if (layoutId === TradingChart.upstoxLayoutId) {
      this.showErrorNotification(
        'FAILED_TO_DELETE',
        'CANNOT_DELETE_UPSTOX_LAYOUT_TEXT',
      );
      return Promise.reject(new Error('Cannot delete Upstox layout'));
    }
    // Remove drawings mapped to the layout
    await this.removeDrawings(layoutId);

    if (Settings.getIsChartSettingsSaveEnabled()) {
      // Remove the layout
      await Settings.deleteChartSettings({
        id: layoutId as string,
        settingsType: SettingsType.SAVING_CHART_LAYOUT,
      });
      this.reloadDefaultLayoutIfNeeded(layoutId);
      return Promise.resolve();
    }

    return new Promise(resolve => {
      // Remove the layout from the local storage
      this.deleteChartInfoStorage(layoutId);
      this.reloadDefaultLayoutIfNeeded(layoutId);
      const chartsList = this.getChartInfoStorage() || {};
      const totalSavedCharts = Object.keys(chartsList)?.length;
      TrackTVChartSaveEvents.sendTotalChartSavedMixpanelEvent(
        totalSavedCharts ?? 0,
        false,
      );
      resolve();
    });
  }

  /**
   * Reloads the default layout if the deleted layout is the current one.
   * This ensures that when a user deletes the active layout,
   * the system triggers a reload to restore a default or fallback layout.
   */
  reloadDefaultLayoutIfNeeded(layoutId: string | number) {
    if (layoutId === TradingChart.currentLayoutId) {
      TradingChart.toggleReloadLayout();
    }
  }

  async removeDrawings(layoutId: string | number) {
    // Get the chart layout content and parse it
    const chartLayoutContent = await this.getChartContentFromStorage(layoutId);

    const contentData = JSON.parse(chartLayoutContent || '{}');
    const chartContentData: ContentType = JSON.parse(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      contentData?.content || '{}',
    );
    const firstChart = chartContentData?.charts?.[0];
    const drawingKey = TradingChartService.getDrawingKey(
      layoutId.toString(),
      firstChart?.chartId,
    );
    const {
      isSavedUsingDrawingKey,
      content,
    } = await this.getLineToolsAndGroupsContent(
      layoutId.toString(),
      firstChart?.chartId,
    );

    if (isSavedUsingDrawingKey) {
      if (content) {
        await this.removeDrawingByDrawingKey(drawingKey);
      }
      // Remove drawings mapped to the layout from the rest of the charts
      await this.removeDrawingsFromChartsLayout(
        layoutId,
        chartContentData?.charts?.slice(1),
      );
    } else if (content) {
      await this.removeDrawingByLayoutId(layoutId);
    }
  }

  async removeDrawingByDrawingKey(drawingKey: string) {
    if (Settings.getIsChartSettingsSaveEnabled()) {
      await Settings.deleteChartSettings({
        id: drawingKey,
        settingsType: SettingsType.SAVING_DRAWINGS,
      });
    } else {
      this.deleteChartDrawingsStorage(drawingKey);
    }
  }

  async removeDrawingsFromChartsLayout(
    layoutId: string | number,
    charts: ContentChartsType,
  ) {
    await Promise.all(
      charts.map(async chart => {
        const drawingKey = TradingChartService.getDrawingKey(
          layoutId.toString(),
          chart?.chartId,
        );
        const { content } = await this.getLineToolsAndGroupsContent(
          layoutId.toString(),
          chart?.chartId,
        );

        if (content) {
          this.removeDrawingByDrawingKey(drawingKey);
        }
      }),
    );
  }

  async removeDrawingByLayoutId(layoutId: string | number) {
    if (Settings.getIsChartSettingsSaveEnabled()) {
      await Settings.deleteChartSettings({
        id: layoutId?.toString(),
        settingsType: SettingsType.SAVING_DRAWINGS,
      });
    } else {
      this.deleteChartDrawingsStorage(layoutId?.toString());
    }
  }

  async saveChart(chartData: ChartData): Promise<string> {
    if (!this.isValidLayoutName(chartData)) {
      return Promise.reject(new Error('Error saving chart'));
    }

    const themeData = {
      theme:
        AppService.theme === 'auto'
          ? getSystemDefaultTheme()
          : THEME_KEYS[AppService.theme],
    };

    if (Settings.getIsChartSettingsSaveEnabled()) {
      TrackTVChartSaveEvents.sendSaveChartWithSizeMixpanelEvent(
        chartData,
        true,
      );
      const contentData = JSON.parse(chartData?.content || '{}');
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const content: ContentType = JSON.parse(contentData?.content || '{}');
      const chartsData = get(content, 'charts', []);
      if (chartsData?.length > 0) {
        const updatedChartsData = await TradingChartService
          .updateDrawingsSymbolWithInstrumentKey(chartsData);
        // Filter out underlying charts' sources.
        const updatedChartsDataWithoutUnderlyingCharts =
          TradingChartService.getFilteredLocalChartState(
            updatedChartsData,
        );

        set(content, 'charts', updatedChartsDataWithoutUnderlyingCharts);
        set(contentData, 'content', JSON.stringify(content));
        set(chartData, 'content', JSON.stringify(contentData));
      }

      const layoutId = await Settings.saveChartSettings({
        id: chartData.id,
        settingsType: SettingsType.SAVING_CHART_LAYOUT,
        data: { ...chartData, ...themeData },
      });
      TradingChart.setCurrentLayoutId(layoutId);
      return Promise.resolve(layoutId);
    }

    return new Promise(resolve => {
      const chartsList = this.getChartInfoStorage() || {};

      // NOTE: If chartData.id is not undefined, then it is "save as".
      const layoutId = chartData.id || FormatService.generateUUID();

      const updatedChartData = {
        ...chartData,
        ...themeData,
        timestamp: Date.now() / 1000,
        id: layoutId,
      };

      this.setChartInfoStorage(layoutId, updatedChartData);
      TradingChart.setCurrentLayoutId(layoutId);

      TrackTVChartSaveEvents.sendSaveChartWithSizeMixpanelEvent(
        chartsList[layoutId],
        false,
      );

      resolve(layoutId);
    });
  }

  // Validate Upstox Layout name restrictions
  isValidLayoutName(chartData: ChartData) {
    const hasUpstoxLayout = !!TradingChart.upstoxLayoutId;
    const isCreatingNewLayout = chartData?.id === undefined;
    const isUpdatingUpstoxLayout =
      hasUpstoxLayout && chartData?.id === TradingChart.upstoxLayoutId;
    const isNamingUpstoxLayout = chartData.name === UPSTOX_LAYOUT_NAME;

    // Creating new layout with Upstox Layout name
    if (isCreatingNewLayout && hasUpstoxLayout && isNamingUpstoxLayout) {
      return this.showErrorNotification(
        'FAILED_TO_SAVE',
        'CANNOT_CREATE_LAYOUT_WITH_UPSTOX_LAYOUT_NAME',
      );
    }
    // Renaming Upstox Layout
    if (isUpdatingUpstoxLayout && !isNamingUpstoxLayout) {
      return this.showErrorNotification(
        'FAILED_TO_SAVE',
        'CANNOT_RENAME_UPSTOX_LAYOUT',
      );
    }
    // Naming a different layout as Upstox
    if (hasUpstoxLayout && !isUpdatingUpstoxLayout && isNamingUpstoxLayout) {
      return this.showErrorNotification(
        'FAILED_TO_SAVE',
        'CANNOT_RENAME_LAYOUT_WITH_NAME_UPSTOX_LAYOUT',
      );
    }
    return true;
  }

  showErrorNotification(
    messageTitle: TranslationKey,
    messageText: TranslationKey,
  ) {
    if (broker.instance) {
      broker.instance.showNotification(
        LocaleService.translate(messageTitle),
        LocaleService.translate(messageText),
        NotificationType.Error,
      );
    }
  }

  async getChartContentFromStorage(layoutId: string | number): Promise<string> {
    let content = '';
    if (Settings.getIsChartSettingsSaveEnabled()) {
      const data = await Settings.getChartSettingsContent({
        id: layoutId as string,
        settingsType: SettingsType.SAVING_CHART_LAYOUT,
      });
      content = data?.content || '';
    } else {
      const chartInfoStorage = this.getChartInfoStorage() || {};
      content = chartInfoStorage[layoutId]?.content;
    }
    return Promise.resolve(content);
  }

  async getChartContent(layoutId: string | number): Promise<string> {
    TradingChart.setCurrentLayoutId(layoutId);
    let content = '';
    let themeChanged = false;
    const currentTheme =
      AppService.theme === 'auto'
        ? getSystemDefaultTheme()
        : THEME_KEYS[AppService.theme];

    if (Settings.getIsChartSettingsSaveEnabled()) {
      const data = await Settings.getChartSettingsContent({
        id: layoutId as string,
        settingsType: SettingsType.SAVING_CHART_LAYOUT,
      });

      themeChanged = data?.theme?.toLowerCase() !== currentTheme;
      content = data?.content || '';

      const contentData = JSON.parse(content);
      const chartContentData: ContentType = JSON.parse(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        contentData?.content || '{}',
      );

      const chartsData = get(chartContentData, 'charts', []);
      if (chartsData?.length > 0) {
        // sources present in the layout are in instrumentKey
        // so update the sources with TV token
        const updatedChartsData =
          await TradingChartService.updateChartDataWithTVToken(chartsData);
        set(chartContentData, 'charts', updatedChartsData);
        set(contentData, 'content', JSON.stringify(chartContentData));
        content = JSON.stringify(contentData);
      }
    } else {
      const chartInfoStorage = this.getChartInfoStorage() || {};
      content = chartInfoStorage[layoutId]?.content;

      themeChanged =
        chartInfoStorage[layoutId]?.theme?.toLowerCase() !== currentTheme;
    }

    if (themeChanged) {
      AppService.setIsChartLayoutUpdated(true);
    }
    return Promise.resolve(content);
  }

  async getAllStudyTemplates(): Promise<StudyTemplateData[]> {
    if (Settings.getIsChartSettingsSaveEnabled()) {
      let studyTemplateList: StudyTemplateData[] = [];

      const chartSettings = await Settings.getChartSettingsList(
        SettingsType.SAVING_STUDY_TEMPLATES,
      );
      if (chartSettings && 'savedStudyTemplates' in chartSettings) {
        studyTemplateList = chartSettings.savedStudyTemplates as
          StudyTemplateData[];
      }
      TrackTVChartSaveEvents.sendTotalTemplatesSavedMixpanelEvent(
        studyTemplateList?.length ?? 0,
        true,
      );

      return studyTemplateList;
    }

    return new Promise(resolve => {
      const studyTemplatesList: StudyTemplateData[] = Object.values(
        this.getChartStudyTemplatesStorage() || {},
      );

      TrackTVChartSaveEvents.sendTotalTemplatesSavedMixpanelEvent(
        studyTemplatesList?.length ?? 0,
        false,
      );

      resolve(studyTemplatesList);
    });
  }

  removeStudyTemplate(
    studyTemplateMetaInfo: StudyTemplateMetaInfo,
  ): Promise<void> {
    if (Settings.getIsChartSettingsSaveEnabled()) {
      return Settings.deleteChartSettings({
        settingsType: SettingsType.SAVING_STUDY_TEMPLATES,
        name: studyTemplateMetaInfo.name,
      });
    }

    return new Promise(resolve => {
      this.deleteChartStudyTemplatesStorage(studyTemplateMetaInfo.name);
      const studyTemplatesList = this.getChartStudyTemplatesStorage() || {};
      const totalSavedTemplates = Object.keys(studyTemplatesList)?.length;
      TrackTVChartSaveEvents.sendTotalTemplatesSavedMixpanelEvent(
        totalSavedTemplates ?? 0,
        false,
      );
      resolve();
    });
  }

  async saveStudyTemplate(studyTemplateData: StudyTemplateData): Promise<void> {
    const saveChartsToServer = Settings.getIsChartSettingsSaveEnabled();

    if (saveChartsToServer) {
      await this.saveStudyTemplateToServer(studyTemplateData);
      return;
    }

    const studyTemplatesList = this.getChartStudyTemplatesStorage() || {};
    const content = get(studyTemplateData, 'content', '{}');
    let contentData: StudyTemplateContentType = JSON.parse(content);

    if (contentData) {
      // For web charts, when saving study templates
      // convert from custom tvExchangeToken to instrumentKey
      contentData = await getUpdatedSavedStudyTemplate(studyTemplateData);
    }

    this.setChartStudyTemplatesStorage(studyTemplateData.name, {
      ...studyTemplateData,
      content: JSON.stringify(contentData),
    });

    TrackTVChartSaveEvents.sendSaveTemplateWithSizeMixpanelEvent(
      studyTemplatesList[studyTemplateData.name],
      false,
    );
  }

  async saveStudyTemplateToServer(
    studyTemplateData: StudyTemplateData,
  ): Promise<void> {
    TrackTVChartSaveEvents.sendSaveTemplateWithSizeMixpanelEvent(
      studyTemplateData,
      true,
    );
    const contentData = await getUpdatedSavedStudyTemplate(studyTemplateData);
    const updatedStudyTemplateData = {
      name: studyTemplateData.name,
      content: JSON.stringify(contentData),
    };

    await Settings.saveChartSettings({
      settingsType: SettingsType.SAVING_STUDY_TEMPLATES,
      data: updatedStudyTemplateData,
    });
  }

  async getStudyTemplateContent(
    studyTemplateMetaInfo: StudyTemplateMetaInfo,
  ): Promise<string> {
    if (Settings.getIsChartSettingsSaveEnabled()) {
      const studyTemplateContent = await Settings.getChartSettingsContent({
        name: studyTemplateMetaInfo.name,
        settingsType: SettingsType.SAVING_STUDY_TEMPLATES,
      });

      const studyTemplateContentData = JSON.parse(
        studyTemplateContent?.content || '{}',
      );
      // For web charts, when loading study templates
      // convert from instrumentKey to custom tvInstrumentKey
      const panes = get(studyTemplateContentData, 'panes', []);
      await TradingChartService.updatePaneSources(panes);
      await updateSymbolWithTVToken(studyTemplateContentData);
      return JSON.stringify(studyTemplateContentData || '');
    }

    const studyTemplatesList = this.getChartStudyTemplatesStorage() || {};
    // For web charts, when loading study templates
    // convert from exchangeToken/instrumentKey to custom tvInstrumentKey
    const { content } = studyTemplatesList[studyTemplateMetaInfo.name];
    const contentData = JSON.parse(content ?? '{}');
    const panes = get(contentData, 'panes', []);
    await TradingChartService.updatePaneSources(panes);
    await updateSymbolWithTVToken(contentData);
    return JSON.stringify(contentData) ?? '';
  }

  getDrawingTemplates(): Promise<string[]> {
    return new Promise(() => ({}));
  }

  loadDrawingTemplate(): Promise<string> {
    return new Promise(() => ({}));
  }

  removeDrawingTemplate(): Promise<void> {
    return new Promise(() => ({}));
  }

  saveDrawingTemplate(): Promise<void> {
    return new Promise(() => ({}));
  }

  getChartTemplateContent(): Promise<ChartTemplate> {
    return new Promise(() => ({}));
  }

  getAllChartTemplates(): Promise<string[]> {
    return new Promise(() => ({}));
  }

  saveChartTemplate(): Promise<void> {
    return new Promise(() => ({}));
  }

  removeChartTemplate(): Promise<void> {
    return new Promise(() => ({}));
  }

  async saveLineToolsAndGroups(
    layoutId: string | undefined,
    chartId: string | number,
    state: LineToolsAndGroupsState,
  ): Promise<void> {
    const drawingsState = cloneDeep(state);
    const sources: Map<EntityId, LineToolState | null> | null = get(
      drawingsState,
      'sources',
      null,
    );
    const drawingKey = TradingChartService.getDrawingKey(layoutId, chartId);

    if (sources) {
      // Accumulate the drawing data
      if (!this.accumulatedDrawings.has(drawingKey)) {
        this.accumulatedDrawings.set(drawingKey, new Map());
      }
      // Update sources with instrument keys and save the drawings
      await this.updateSourcesWithInstrumentKey(drawingKey, sources);

      // Call the debounced save method
      this.debouncedSaveDrawings();
    }

    return Promise.resolve();
  }

  async updateSourcesWithInstrumentKey(
    drawingKey: string,
    sources: Map<EntityId, LineToolState | null>,
  ) {
    const accumulatedDrawingSources = this.accumulatedDrawings.get(drawingKey);
    if (accumulatedDrawingSources) {
      const clonedSources = cloneDeep(sources);
      const updateSourcesWithInstrumentKey = Array.from(clonedSources).map(
        async ([key, value]) => {
          if (value !== null) {
            const tvExchangeToken = get(value, 'symbol', '');
            const instrumentKey = await TradingChartService
              .convertTVTokenToInstrumentKey(tvExchangeToken);
            set(value, 'symbol', instrumentKey);
            set(value, 'state.state.symbol', instrumentKey);
            accumulatedDrawingSources.set(key, value);
          }
        },
      );
      await Promise.all(updateSourcesWithInstrumentKey);
    }
  }

  async saveDrawings() {
    try {
      const saveDrawings = Array.from(this.accumulatedDrawings).map(
        async ([drawingKey, sources]) => {
          try {
          const { filteredSources, isDrawingsChanged } =
            this.getFilteredSourcesWithTimestamp(drawingKey, sources);
          if (!isDrawingsChanged) {
            return Promise.resolve();
          }

          const data = {
            id: drawingKey,
            content: JSON.stringify({ sources: filteredSources }),
          };
          if (Settings.getIsChartSettingsSaveEnabled()) {
            await Settings.saveChartSettings({
              settingsType: SettingsType.SAVING_DRAWINGS,
              id: drawingKey,
              data,
              loadingKey: `CHART_SETTING_DATA_LOADING_${drawingKey}` as LoadingKey,
            });
          } else {
            this.setChartDrawingsStorage(drawingKey, data);
          }
          } catch (error) {
            Logger.error(error);
          }
        },
      );

      await Promise.all(saveDrawings);
    } finally {
      // Clear the accumulated drawings after saving
      this.accumulatedDrawings.clear();
    }
  }

  getFilteredSourcesWithTimestamp(
    drawingKey: string,
    clonedSources: Map<EntityId, LineToolState | null>,
  ) {
    // No need to process further if localStorage is enabled
    // And irrespective of whether drawings are changed or not,
    // always save drawings when on localStorage
    if (!Settings.getIsChartSettingsSaveEnabled()) {
      return {
        filteredSources: Object.fromEntries(clonedSources),
        isDrawingsChanged: true,
      };
    }
    let savedGlobalDrawingSources = LStorage.getItem(
      'TV_CHART_DRAWING_SOURCES_OLD',
    ) || {};
    const savedDrawingSources = get(
      savedGlobalDrawingSources,
      drawingKey,
      {},
    );

    // Create a new object to store updated drawing sources.
    // This ensures local storage reflects the latest state, as savedDrawingSources
    // is a reference to the old object. If drawings are deleted, local storage must be updated accordingly.
    const newSavedDrawingSources = {};
    let isAnyDrawingChanged = false;

    // Check if drawing is removed
    const clonedSourcesKeys = new Set(Array.from(clonedSources.keys()));
    Object.keys(savedDrawingSources).forEach((key) => {
      if (!clonedSourcesKeys.has(key as EntityId)) {
        // Drawing was deleted - mark as changed
        isAnyDrawingChanged = true;
      }
    });
    Array.from(clonedSources).forEach(
      ([key, value]) => {
        if (value !== null) {
          // Setting the timestamp helps track the creation time,
          // enabling the removal of older drawings when the storage limit is reached.
          const isDrawingChanged =
            this.updateTimestampAndCheckIfDrawingsChanged(
              value,
              savedDrawingSources,
              key,
            );
          if (isDrawingChanged) {
            // Even if a single drawing source is changed, set isDrawingsChanged to true
            isAnyDrawingChanged = true;
          }
        }
        // Set the value outside the condition to ensure it gets updated in local storage,
        // even when null (e.g., during removeDrawings).
        set(newSavedDrawingSources, key, value);
      },
    );
    const {
      filteredSources,
      removedDrawingSourceIds,
    } = removeOlderDrawingSourcesIfNeeded(
      clonedSources,
      newSavedDrawingSources,
    );
    // Fetch the newSavedDrawingSources from LS again to get latest state
    savedGlobalDrawingSources =
      LStorage.getItem('TV_CHART_DRAWING_SOURCES_OLD') || {};
    set(savedGlobalDrawingSources, drawingKey, newSavedDrawingSources);
    LStorage.setItem('TV_CHART_DRAWING_SOURCES_OLD', savedGlobalDrawingSources);
    // removedDrawingSourceIds keeps track of all drawings that need to be removed.
    // The API call will save drawings with filteredSources,
    // but we also need to remove the drawings from the TV state.
    if (removedDrawingSourceIds?.length > 0) {
      TradingChartService.setRemovedDrawingSourceIds(removedDrawingSourceIds);
    }
    return {
      filteredSources: Object.fromEntries(filteredSources),
      isDrawingsChanged: isAnyDrawingChanged,
    };
  }

  updateTimestampAndCheckIfDrawingsChanged(
    drawingSource: LineToolState,
    savedDrawingSources: Record<string, LineToolState | null>,
    key: string,
  ) {
    const newTimestamp = Date.now();
    const savedDrawingSource = get(savedDrawingSources, key, null);
    // If the drawing source is not present in LS (meaning it is a new drawing source),
    // then set current time as timestamp
    if (!savedDrawingSource) {
      set(drawingSource, 'timestamp', newTimestamp);
      return true;
    }
      // Update the timetstamp with the one present in LS
      set(drawingSource, 'timestamp', get(savedDrawingSource, 'timestamp', newTimestamp));
      try {
        // Even after setting the same timestamp as LS, if objects don't match,
        // then it means the drawing source has changed. So set current timestamp
        const savedDrawingSourceParsed =
          JSON.parse(JSON.stringify(savedDrawingSource));
        const drawingSourceParsed =
          JSON.parse(JSON.stringify(drawingSource));
          if (!isEqual(savedDrawingSourceParsed, drawingSourceParsed)) {
            set(drawingSource, 'timestamp', newTimestamp);
            return true;
          }
      } catch (error) {
        Logger.error('Error parsing drawing sources for comparison: ', error);
      }
      return false;
  }

  async loadLineToolsAndGroups(
    layoutId: string | undefined,
    chartId: string | number,
    requestType: LineToolsAndGroupsLoadRequestType,
  ): Promise<Partial<LineToolsAndGroupsState> | null> {
    const now = Date.now();
    const key = `${layoutId}${chartId}${requestType}`;
    const { lastRequestTime, promiseCache } = this.loadLineToolsRequestCache;

    // If a call was made within the delay window, return the cached promise
    if (
      promiseCache?.[key] != null &&
      now - lastRequestTime < LINE_TOOL_GROUPS_THROTTLE
    ) {
      return Promise.resolve(promiseCache[key]);
    }

    // Update the last called time
    this.loadLineToolsRequestCache.lastRequestTime = now;

    // Call the original function and cache the result
    this.loadLineToolsRequestCache.promiseCache[
      key
    ] = this.getLoadLineToolsAndGroups(layoutId, chartId, requestType).finally(
      () => {
        // Delete the cache after the delay
        setTimeout(() => {
          delete this.loadLineToolsRequestCache.promiseCache[key];
        }, LINE_TOOL_GROUPS_THROTTLE);
      },
    );

    return this.loadLineToolsRequestCache.promiseCache[key];
  }

  async getLoadLineToolsAndGroups(
    layoutId: string | undefined,
    chartId: string | number,
    requestType: LineToolsAndGroupsLoadRequestType,
  ): Promise<Partial<LineToolsAndGroupsState> | null> {
    if (requestType !== 'lineToolsWithoutSymbol' || !layoutId || !chartId) {
      return null;
    }

    const { content } = await this.getLineToolsAndGroupsContent(
      layoutId,
      chartId,
    );

    const rawSources = TradingChartService.getLineToolsAndGroupsSources(
      content,
    );

    if (isEmpty(rawSources)) {
      return null;
    }
    if (Settings.getIsChartSettingsSaveEnabled()) {
      this.saveDrawingSourcesInStorage(layoutId, chartId, rawSources);
    }
    const updatedSources =
      await TradingChartService.updateDrawingsSourcesWithTVToken(
        rawSources,
      );
    return Promise.resolve({
      sources: updatedSources,
    });
  }

  async getLineToolsAndGroupsContent(
    layoutId: string,
    chartId: string | number,
  ): Promise<{
    content: string;
    isSavedUsingDrawingKey: boolean;
  }> {
    const drawingKey = TradingChartService.getDrawingKey(layoutId, chartId);
    let isSavedUsingDrawingKey = true;

    if (Settings.getIsChartSettingsSaveEnabled()) {
      let data = await Settings.getChartSettingsContent({
        id: drawingKey,
        settingsType: SettingsType.SAVING_DRAWINGS,
        loadingKey: `CHART_SETTING_DATA_LOADING_${drawingKey}` as LoadingKey,
      });

      if (!data?.content) {
        // `Previously drawings were saved with layoutId as key`
        data = await Settings.getChartSettingsContent({
          id: layoutId,
          settingsType: SettingsType.SAVING_DRAWINGS,
          loadingKey: `CHART_SETTING_DATA_LOADING_${drawingKey}` as LoadingKey,
        });

        isSavedUsingDrawingKey = false;
      }

      return {
        content: data?.content || '',
        isSavedUsingDrawingKey,
      };
    }

    const drawings = this.getChartDrawingsStorage() || {};
    let content = get(drawings?.[drawingKey], 'content');

    if (!content) {
      // `Previously drawings were saved with layoutId as key`
      content = get(drawings?.[layoutId], 'content');
      isSavedUsingDrawingKey = false;
    }

    return {
      content: content || '',
      isSavedUsingDrawingKey,
    };
  }

  // Storing drawing sources in LS because when TV calls saveLineToolsAndGroups,
  // it does not persist the timestamps added to existing drawing sources.
  // This ensures that the state retains sources with timestamps.
  saveDrawingSourcesInStorage(
    layoutId: string,
    chartId: string | number,
    rawSources: Record<string, LineToolState | null>,
  ) {
    const savedGlobalDrawingSources = LStorage.getItem('TV_CHART_DRAWING_SOURCES_OLD') || {};
    const drawingKey = TradingChartService.getDrawingKey(layoutId, chartId);
    set(savedGlobalDrawingSources, drawingKey, rawSources);
    LStorage.setItem('TV_CHART_DRAWING_SOURCES_OLD', savedGlobalDrawingSources);
  }

  async saveEmptyUpstoxLayout() {
    const chartData: ChartData = this.generateEmptyUpstoxLayout();
    await this.saveChart(chartData);
  }

  generateEmptyUpstoxLayout(): ChartData {
    return {
      content: JSON.stringify({
        content: JSON.stringify(UPSTOX_LAYOUT_DEFAULT_CHART_CONTENT),
        description: '',
        is_realtime: '1',
        name: UPSTOX_LAYOUT_NAME,
        resolution: DEFAULT_CHART_INTERVAL_TV as ResolutionString,
        short_name: DEFAULT_TOKEN_SHORT_NAME,
        symbol: DEFAULT_TOKEN,
      }),
      id: undefined,
      name: UPSTOX_LAYOUT_NAME,
      resolution: DEFAULT_CHART_INTERVAL_TV as ResolutionString,
      symbol: DEFAULT_TOKEN_SHORT_NAME,
    };
  }

  private buildChartData(userRecentLayout: ContentType): ChartData {
    const chartsData = get(userRecentLayout, 'charts', []);
    const updatedChartsData = cloneDeep(chartsData);

    set(userRecentLayout, 'charts', updatedChartsData);
    const mainSeriesPane = TradingChartService.getMainSeriesPane(
      userRecentLayout.charts[0].panes,
    );
    const mainSeriesSource = TradingChartService.getMainSeriesSource(
      mainSeriesPane?.sources || [],
    );

    const resolution = get(
      mainSeriesSource,
      'state.interval',
      DEFAULT_CHART_INTERVAL_TV,
    );
    const symbol = get(mainSeriesSource, 'state.symbol', DEFAULT_TOKEN);
    const shortName = get(mainSeriesSource, 'state.shortName', '');
    return {
      id: undefined,
      content: JSON.stringify({
        content: JSON.stringify(userRecentLayout),
        description: '',
        is_realtime: '1',
        name: UPSTOX_LAYOUT_NAME,
        resolution: resolution as ResolutionString,
        short_name: shortName,
        symbol,
      }),
      name: UPSTOX_LAYOUT_NAME,
      resolution: resolution as ResolutionString,
      symbol: shortName,
    };
  }

  async moveRecentLayoutToSavedLayout() {
    const allCharts = await this.getAllCharts();
    if (TradingChart.upstoxLayoutId) return;

    AnalyticsService.sendEvent(EVENTS.MOVE_RECENT_TO_UPSTOX_LAYOUT);
    const userRecentLayout = await RecentLayoutService.getRecentLayout();
    // If the number of saved layouts exceeds the limit, remove the oldest layout
    if (Settings.getIsChartSettingsSaveEnabled()) {
      if (allCharts.length >= TOTAL_ALLOWED_LAYOUTS) {
        await this.removeChart(allCharts[0].id);
      }

      if (userRecentLayout) {
        // This will only happen during chart sync
        const isRecentLayoutExceedingSizeLimit =
          getStringSizeInKb(JSON.stringify(userRecentLayout) || '') >
        AppService.config.chart_content_size_limit_in_kb;

        if (isRecentLayoutExceedingSizeLimit) {
          AnalyticsService.sendEvent(EVENTS.SAVING_EMPTY_UPSTOX_LAYOUT, {
            isRecentLayoutExceedingSizeLimit: true,
          });
          await this.saveEmptyUpstoxLayout();
          return;
        }
      }
    }

    // If no recent layout save an empty upstox layout
    if (!userRecentLayout) {
      AnalyticsService.sendEvent(EVENTS.SAVING_EMPTY_UPSTOX_LAYOUT, {
        isRecentLayoutEmpty: true,
      });
      await this.saveEmptyUpstoxLayout();
      return;
    }

    try {
      const chartData = this.buildChartData(userRecentLayout);
      await this.saveChart(chartData);
    } catch (error) {
      // If the layout is invalid, save an empty upstox layout
      AnalyticsService.sendEvent(EVENTS.SAVING_EMPTY_UPSTOX_LAYOUT, {
        isError: true,
      });
      await this.saveEmptyUpstoxLayout();
    }
  }
}

export default SaveLoadAdapter;
